/**
 * WorkOrderBuildOrderTab
 * ----------------------
 * Hospital PO capture for a work order — the maintenance twin of the
 * backorder Build Order tab. The PO number saved here rides on every
 * milestone email and on both vendor POs.
 *
 * PATCH /api/admin/work-orders/{id}/po
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WorkOrderBuildOrderTab({ workOrder, token, onRefresh }) {
  const headers = { Authorization: `Bearer ${token}` };
  const co = workOrder?.customer_order || {};
  const [form, setForm] = useState({
    po_number: co.po_number || workOrder?.po_number || "",
    po_date: co.po_date || "",
    nte_amount: co.nte_amount ?? "",
    billing_contact: co.billing_contact || workOrder?.contact_name || "",
    billing_email: co.billing_email || workOrder?.contact_email || "",
    notes: co.notes || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const c = workOrder?.customer_order || {};
    setForm((p) => ({
      ...p,
      po_number: c.po_number || workOrder?.po_number || p.po_number,
      po_date: c.po_date || p.po_date,
      nte_amount: c.nte_amount ?? p.nte_amount,
    }));
  }, [workOrder]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.po_number.trim()) return toast.error("Enter the hospital PO number.");
    setSaving(true);
    try {
      await axios.patch(`${API}/api/admin/work-orders/${workOrder.id}/po`, form, { headers });
      toast.success("Hospital PO saved", {
        description: "It now appears on every milestone email and on both vendor POs.",
      });
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the PO");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="wo-build-order-tab">
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <h3 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
          <Receipt className="w-4 h-4 text-slate-600" /> Hospital Purchase Order
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          The hospital&rsquo;s PO authorizes the work. Save it here and it rides on the work-order
          acknowledgment, every milestone email, the contractor PO and the supplier parts PO.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Hospital PO # *">
          <Input value={form.po_number} onChange={(e) => set("po_number", e.target.value)}
            placeholder="4500123456" data-testid="wo-po-number-input" />
        </Field>
        <Field label="PO date">
          <Input type="date" value={form.po_date} onChange={(e) => set("po_date", e.target.value)}
            data-testid="wo-po-date-input" />
        </Field>
        <Field label="Not-to-exceed amount ($)">
          <Input value={form.nte_amount} onChange={(e) => set("nte_amount", e.target.value)}
            placeholder="2400" data-testid="wo-nte-input" />
        </Field>
        <Field label="Billing contact">
          <Input value={form.billing_contact} onChange={(e) => set("billing_contact", e.target.value)}
            data-testid="wo-billing-contact-input" />
        </Field>
        <Field label="Billing email">
          <Input value={form.billing_email} onChange={(e) => set("billing_email", e.target.value)}
            data-testid="wo-billing-email-input" />
        </Field>
      </div>

      <Field label="Internal notes on this PO">
        <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
          data-testid="wo-po-notes-input" />
      </Field>

      <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800"
        data-testid="wo-po-save-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
        Save purchase order
      </Button>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
      {label}
    </label>
    {children}
  </div>
);
